import { useMutation, useQuery } from "@apollo/client";
import React from "react";
import { Form, Input, Button, Card, Space, Table } from "antd";
import { TAXONOMY_QUERY, ToHumanReadable } from "~/context/taxonomy-context";
import { UPDATE_TAXONOMY_MUTATION } from "./gql";
import styles from "./taxonomy-container.module.css";
import { Taxonomy } from "~/gql/graphql";
import { showAutoClosingAlert } from "~/design-system";

const { ["container"]: containerClassName } = styles as any;

export const TaxonomySettingsContainer = () => {
  const { data, loading } = useQuery(TAXONOMY_QUERY);
  const [updateTaxonomy] = useMutation(UPDATE_TAXONOMY_MUTATION);
  const [editingKey, setEditingKey] = React.useState<number | null>(null);
  const [form] = Form.useForm();

  const isEditing = (record: Taxonomy) => record.id === editingKey;

  const edit = (record: Taxonomy) => {
    form.setFieldsValue({ value: record.value });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey(null);
  };

  const save = async (record: Taxonomy) => {
    try {
      const value = await form.validateFields();
      await updateTaxonomy({
        variables: {
          value: value.value,
          key: record.key,
          updateTaxonomyId: record.id,
        },
        refetchQueries: [TAXONOMY_QUERY],
      });
      setEditingKey(null);
    } catch (error) {
      return showAutoClosingAlert({
        message: "Failed to update label",
        type: "error",
      });
    }
  };

  const columns = [
    {
      title: "Default",
      dataIndex: "key",
      width: 400,
      key: "key",
      render: (_: any, record: Taxonomy) => {
        return ToHumanReadable(record.key);
      },
    },
    {
      title: "Custom",
      dataIndex: "value",
      width: 400,
      key: "value",
      render: (_: any, record: Taxonomy) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="value"
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Value is required" }]}
          >
            <Input />
          </Form.Item>
        ) : (
          record.value
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_: any, record: Taxonomy) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button type="link" onClick={() => save(record)}>
              Save
            </Button>
            <Button type="link" onClick={cancel}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Button type="link" disabled={editingKey !== null} onClick={() => edit(record)}>
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <div className={containerClassName}>
      <Card title="Custom Labels">
        <Form form={form} component={false}>
          <Table
            loading={loading}
            dataSource={data?.taxonomy}
            columns={columns}
            rowKey="id"
            pagination={false}
          />
        </Form>
      </Card>
    </div>
  );
};
